import { useState, useEffect, useRef } from 'react'
import { Input } from './Input'
import { api } from '@/lib/api/client'

interface UrlAutocompleteProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

interface SitemapUrl {
  loc: string
}

export function UrlAutocomplete({ value, onChange, placeholder }: UrlAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<SitemapUrl[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    try {
      const response = await api.get<{ urls: SitemapUrl[] }>(`/sitemap?q=${encodeURIComponent(query)}`)
      if (response.success && response.data?.urls) {
        setSuggestions(response.data.urls)
        setIsOpen(true)
      }
    } catch {
      setSuggestions([])
    }
    setIsLoading(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue)
    }, 300)
  }

  const handleSelectSuggestion = (url: string) => {
    onChange(url)
    setIsOpen(false)
    setSuggestions([])
  }

  const extractSlug = (url: string): string => {
    try {
      const path = new URL(url).pathname
      return path.replace(/^\/|\/$/g, '')
    } catch {
      return url
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        type="url"
        value={value}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        placeholder={placeholder || 'https://... (digita per cercare)'}
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
        </div>
      )}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.loc}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
              onClick={() => handleSelectSuggestion(suggestion.loc)}
            >
              <span className="font-medium text-gray-900">{extractSlug(suggestion.loc)}</span>
              <span className="block text-xs text-gray-500 truncate">{suggestion.loc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
